/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { ActivityItem } from '@shared/types/dashboard';
import { Package, FileText, Bug, Clock } from 'lucide-react';

interface ActivityCardProps {
  title: string;
  icon: 'product' | 'user-story' | 'issue';
  activities: ActivityItem[];
  isLoading?: boolean;
}

export function ActivityCard({ title, icon, activities, isLoading }: ActivityCardProps) {
  // Get icon component
  const IconComponent = icon === 'product' ? Package : icon === 'user-story' ? FileText : Bug;
  
  // Calculate summary stats
  const stats = {
    created: activities.filter(a => a.changeType === 'created').length,
    updated: activities.filter(a => a.changeType === 'updated').length,
    completed: activities.filter(a => a.changeType === 'completed').length
  };

  // Format relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return then.toLocaleDateString();
  };

  // Get badge color for change type
  const getBadgeStyle = (changeType: string) => {
    const styles = {
      created: { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
      updated: { background: '#fed7aa', color: '#92400e', border: '1px solid #fdba74' },
      completed: { background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }
    };
    return styles[changeType as keyof typeof styles] || styles.updated;
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '20px',
      height: '400px',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #f0f0f0'
      }}>
        <IconComponent size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{title}</h3>
      </div>

      {/* Summary Stats */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '16px',
        flexWrap: 'wrap'
      }}>
        <div style={{ 
          padding: '6px 12px', 
          background: '#dbeafe', 
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: '500',
          color: '#1e40af'
        }}>
          {stats.created} Created
        </div>
        <div style={{ 
          padding: '6px 12px', 
          background: '#fed7aa', 
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: '500',
          color: '#92400e'
        }}>
          {stats.updated} Updated
        </div>
        <div style={{ 
          padding: '6px 12px', 
          background: '#d1fae5', 
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: '500',
          color: '#065f46'
        }}>
          {stats.completed} Completed
        </div>
      </div>

      {/* Activity List */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto',
        minHeight: 0
      }}>
        {isLoading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            color: '#999' 
          }}>
            <Clock size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px', 
            color: '#999' 
          }}>
            <IconComponent size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>No recent activity</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activities.slice(0, 10).map((activity, index) => (
              <div 
                key={`${activity.id}-${index}`}
                style={{
                  padding: '10px',
                  background: '#f9f9f9',
                  borderRadius: '4px',
                  border: '1px solid #e9e9e9',
                  fontSize: '13px'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '4px'
                }}>
                  <span 
                    style={{
                      padding: '2px 8px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      ...getBadgeStyle(activity.changeType)
                    }}
                  >
                    {activity.changeType}
                  </span>
                  <span style={{ 
                    fontSize: '11px', 
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Clock size={12} />
                    {getRelativeTime(activity.timestamp)}
                  </span>
                </div>
                <div style={{ 
                  fontWeight: '500', 
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {activity.title}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {activities.length > 10 && (
        <div style={{ 
          marginTop: '12px', 
          paddingTop: '12px', 
          borderTop: '1px solid #e9e9e9',
          textAlign: 'center',
          fontSize: '12px',
          color: '#666'
        }}>
          Showing 10 of {activities.length} activities
        </div>
      )}
    </div>
  );
}

